// src/pages/admin/dashboard.tsx
// REFACTORIZADO: Usa PageTitle y LoadingStates para consistencia visual

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';

import UserNav from '../../components/navbar/user-nav';
import AdminSidebar from '../../components/admin-sidebar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import ClientOne from '../../components/client-one';

// Componentes reutilizables
import PageTitle from '../../components/page-title';
import { FullPageLoading, SkeletonStats, SkeletonCard, InlineLoading } from '../../components/loading-states';
import LeadsDashboard from '../../components/LeadsDashboard';

export default function Dashboard() {
  const [show, setShow] = useState(false);
  const [perfil, setPerfil] = useState<any>(null);
  const [nombreInmobiliaria, setNombreInmobiliaria] = useState('');
  const [numPropiedades, setNumPropiedades] = useState(0);
  const [calificacion, setCalificacion] = useState(0);
  const [asistencias, setAsistencias] = useState(0);  // Ahora cuenta talleres completados
  const [creditosTotales, setCreditosTotales] = useState(0);
  const [loadingPerfil, setLoadingPerfil] = useState(true);

  const { user, loading: userLoading } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    setLoadingPerfil(true);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const tipos = ['asesor', 'asociado'];
  
        for (const tipo of tipos) {
          const q = query(collection(db, tipo), where('correo', '==', user.email));
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const docSnap = snapshot.docs[0];
            const data = docSnap.data();
            const perfil_id = docSnap.id;
            setPerfil({ ...data, tipo, perfil_id });
  
            // Inmobiliaria - usando doc.id de Firestore
            const inmobiliariaDocId = tipo === 'asesor'
              ? (Array.isArray(data.inmobiliaria_doc_ids) ? data.inmobiliaria_doc_ids[0] : data.inmobiliaria_doc_ids)
              : data.inmobiliaria_doc_id;

            console.log('🏢 Buscando inmobiliaria con doc.id:', inmobiliariaDocId);

            if (inmobiliariaDocId) {
              try {
                const { doc: docFn, getDoc } = await import('firebase/firestore');
                const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
                const inmoSnap = await getDoc(inmoDocRef);
                if (inmoSnap.exists()) {
                  const inmoData = inmoSnap.data();
                  setNombreInmobiliaria(inmoData.nombre || '');
                  console.log('✅ Inmobiliaria encontrada:', inmoData.nombre);
                } else {
                  console.warn('⚠️ Inmobiliaria no encontrada con doc.id:', inmobiliariaDocId);
                }
              } catch (error) {
                console.error('❌ Error al obtener la inmobiliaria:', error);
              }
            }

            // Contar propiedades del asesor/asociado usando doc.id de Firestore
            try {
              const perfilDocId = docSnap.id; // Usar el doc.id de Firestore del asesor/asociado
              console.log('🔍 Buscando propiedades para perfil doc.id:', perfilDocId);

              const qProps = query(
                collection(db, 'propiedades'),
                where('asesor_doc_ids', 'array-contains', perfilDocId)
              );
              const snapProps = await getDocs(qProps);
              setNumPropiedades(snapProps.size);
              console.log(`✅ Propiedades encontradas para ${data.nombre}:`, snapProps.size);

              if (snapProps.size === 0) {
                console.warn('⚠️ No se encontraron propiedades. Verificar que las propiedades tengan el campo asesor_doc_ids con el doc.id:', perfilDocId);
              }
            } catch (error) {
              console.error('❌ Error al contar propiedades:', error);
            }
  
            setCalificacion(data.calificacion || 0);
            // Asistencias se calculará después de cargar talleres completados
  
            // 🧮 Talleres y Créditos
            const qInscripcion = query(collection(db, 'inscripcion'), where('perfil_id', '==', perfil_id));
            const snapInscripcion = await getDocs(qInscripcion);
            let totalCreditos = 0;
  
            const talleresData = await Promise.all(
              snapInscripcion.docs.map(async (docSnap) => {
                const inscripcion = docSnap.data();
                const inscripcionId = docSnap.id;

                // ✅ Usar taller_doc_id y acceso directo por doc.id
                let tallerInfo = null;
                if (inscripcion.taller_doc_id) {
                  try {
                    const { doc: docFn, getDoc } = await import('firebase/firestore');
                    const tallerDocRef = docFn(db, 'taller', inscripcion.taller_doc_id);
                    const tallerSnap = await getDoc(tallerDocRef);
                    if (tallerSnap.exists()) {
                      tallerInfo = tallerSnap.data();
                    }
                  } catch (error) {
                    console.error('Error al cargar taller:', error);
                  }
                }
  
                let creditos = 0;
  
                if (inscripcion.completado) {
                  if (typeof inscripcion.creditos === 'number') {
                    creditos = inscripcion.creditos;
                  } else if (tallerInfo && typeof tallerInfo.creditos === 'number') {
                    creditos = tallerInfo.creditos;
  
                    // Reparación automática: actualizar inscripción
                    try {
                      await updateDoc(doc(db, 'inscripcion', inscripcionId), {
                        creditos
                      });
                    } catch (err) {
                      console.warn('No se pudo actualizar inscripción:', err);
                    }
                  }
  
                  totalCreditos += creditos;
                }
  
                return tallerInfo
                  ? {
                      ...tallerInfo,
                      estatus: inscripcion.completado ? 'Completado' : 'En progreso',
                      codigo_qr: inscripcion.codigo_qr,
                      asistencia: inscripcion.asistencia,
                      inscripcion_id: inscripcionId
                    }
                  : null;
              })
            );
  
            const talleresValidos = talleresData.filter(Boolean);
            setCreditosTotales(totalCreditos);

            // Contar solo talleres completados para "Talleres Asistidos"
            const talleresCompletados = talleresValidos.filter(t => t && t.estatus === 'Completado');
            setAsistencias(talleresCompletados.length);
            setLoadingPerfil(false);
            break;
          }
        }
        // Si no se encontró perfil en ninguna colección
        setLoadingPerfil(false);
      } else {
        setLoadingPerfil(false);
      }
    });

    return () => unsubscribe();
  }, []);
  

  // Estado de carga inicial
  if (userLoading || loadingPerfil) {
    return (
      <>
        <UserNav nombreUsuario={perfil?.nombre} nombreInmobiliaria={nombreInmobiliaria} />
        <PageTitle
          title="Cargando Dashboard..."
          subtitle="Obteniendo tu información"
        />
        <section className="py-5">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-3 col-md-12">
                <SkeletonCard variant="user" className="mb-4" />
              </div>
              <div className="col-lg-9 col-md-12">
                <SkeletonStats count={4} className="mb-4" />
                <SkeletonCard variant="simple" className="mb-4" />
              </div>
            </div>
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  return (
    <>
      <UserNav nombreUsuario={perfil?.nombre} nombreInmobiliaria={nombreInmobiliaria} />

      <PageTitle
        title={`¡Bienvenido${perfil?.nombre ? `, ${perfil.nombre.split(' ')[0]}` : ''}!`}
        subtitle="Aquí comienza tu gestión profesional. Accede a tus propiedades, estadísticas, capacitaciones y más."
      />

      <section className="py-5">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="filter_search_opt">
                <Link to="#" className="btn btn-dark full-width mb-4" onClick={() => setShow(!show)}>
                  Dashboard Navigation<i className="fa-solid fa-bars ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 col-md-12">
              <AdminSidebar show={show} setShow={setShow} nombre={perfil?.nombre} inmobiliaria={nombreInmobiliaria} />
            </div>

            <div className="col-lg-9 col-md-12">
              <div className="row">
                <div className="col-lg-3 col-md-6 col-sm-12">
                  <div className="dashboard-stat widget-1">
                    <div className="dashboard-stat-content">
                      <h4>{numPropiedades}</h4> <span>Propiedades Activas</span>
                    </div>
                    <div className="dashboard-stat-icon"><i className="fa-solid fa-location-dot"></i></div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12">
                  <div className="dashboard-stat widget-2">
                    <div className="dashboard-stat-content">
                      <h4>{asistencias}</h4> <span>Talleres Asistidos</span>
                    </div>
                    <div className="dashboard-stat-icon"><i className="ti-pie-chart"></i></div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12">
                  <div className="dashboard-stat widget-3">
                    <div className="dashboard-stat-content">
                      <h4>{calificacion ? (calificacion / 20).toFixed(1) : '0.0'}/5</h4> <span>Calificación</span>
                    </div>
                    <div className="dashboard-stat-icon"><i className="ti-user"></i></div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12">
                  <div className="dashboard-stat widget-4">
                    <div className="dashboard-stat-content">
                      <h4>{creditosTotales}</h4> <span>Créditos Acumulados</span>
                    </div>
                    <div className="dashboard-stat-icon"><i className="fa-solid fa-certificate"></i></div>
                  </div>
                </div>
              </div>

              {/* Widget de Leads - Solo si tiene perfil */}
              {perfil?.perfil_id && (
                <div className="row mb-4">
                  <div className="col-12">
                    <LeadsDashboard
                      asesorId={perfil.perfil_id}
                      variant="widget"
                    />
                  </div>
                </div>
              )}

              {/* Banner de acceso al Panel de Comité - Solo para admins de comité */}
              {user?.esAdminComite && (
                <div
                  className="card border-0 rounded-4 shadow mb-4"
                  style={{
                    background: 'linear-gradient(135deg, #0c2339 0%, #1a4a7a 100%)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => navigate('/admin-comite/dashboard')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(12,35,57,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 .5rem 1rem rgba(0,0,0,.15)';
                  }}
                >
                  <div className="card-body d-flex align-items-center justify-content-between p-4">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle bg-white d-flex align-items-center justify-content-center me-4"
                        style={{ width: '60px', height: '60px' }}
                      >
                        <i className="fa-solid fa-users-gear fa-2x" style={{ color: '#0c2339' }}></i>
                      </div>
                      <div>
                        <h4 className="text-white mb-1">Panel de Administración de Comité</h4>
                        <p className="text-white-50 mb-0">
                          Gestiona talleres, eventos, inscritos y asistencia
                        </p>
                      </div>
                    </div>
                    <div>
                      <button className="btn btn-light btn-lg px-4">
                        <i className="fa-solid fa-arrow-right me-2"></i>
                        Acceder
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="card border-0 rounded-4 shadow-sm mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">Mi Perfil</h5>
                </div>
                <div className="card-body" style={{ backgroundColor: '#ffffff' }}>
                  {perfil ? (
                    <form className="taller-form-modern">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Nombre</label>
                          <input type="text" className="form-control" defaultValue={perfil.nombre || ''} readOnly />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Email</label>
                          <input type="email" className="form-control" defaultValue={perfil.correo || ''} readOnly />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Teléfono</label>
                          <input type="text" className="form-control" defaultValue={perfil.telefono || ''} readOnly />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Tipo de Perfil</label>
                          <input type="text" className="form-control" defaultValue={perfil.tipo || ''} readOnly />
                        </div>
                        <div className="form-group col-md-12">
                          <label>Inmobiliaria Asignada</label>
                          <input type="text" className="form-control" defaultValue={nombreInmobiliaria} readOnly />
                        </div>
                      </div>
                    </form>
                  ) : (
                    <div className="text-center py-4">
                      <InlineLoading text="Cargando perfil" />
                    </div>
                  )}
                </div>
              </div>

              {perfil && (
                <div className="card border-0 rounded-4 shadow-sm mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">Historial de Reseñas</h5>
                  </div>
                  <div className="card-body" style={{ backgroundColor: '#ffffff' }}>
                    <ClientOne
                      perfilId={
                        perfil.tipo === 'asesor'
                          ? perfil.asesor_id
                          : perfil.tipo === 'asociado'
                          ? perfil.asociado_id
                          : ''
                      }
                    />
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
  